'use client';

import { Input, Flex } from '@/_components';
import {
  useContext,
  useRef,
  useEffect,
  useState,
  useMemo,
  type FC,
} from 'react';
import { ClientQuestionnaireContext } from '@/_contexts/client/ClientQuestionnaireProvider';
import { useDebounce } from '@/_hooks/useDebounce';
import { PreviewContext } from '@/_contexts/PreviewProvider';
import { usePrevious } from '@/_hooks/usePrevious';
import { UserPreviewContext } from '@/_contexts/admin/UserPreviewProvider';
import { SAVE_TEXT_DELAY_MS } from '@/_constants/questions';
import { COUNTRY_OPTIONS } from '@/_constants/phone_numbers';
import { formatPhoneNumber, parseInitialPhone } from '@/_utils/phoneNumbers';
import { validateEmail } from '@/_utils/validations';
import type { QuestionFormTextProps } from './QuestionFormText.types';

export const QuestionFormText: FC<QuestionFormTextProps> = ({
  question,
  mode,
  isDisabled,
}) => {
  const [isSaved, setSaved] = useState<boolean>(false);
  const [isMounted, setMounted] = useState<boolean>(false);
  const [isBlurred, setBlurred] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const { updateQuestion } = useContext(ClientQuestionnaireContext);
  const { updatePreviewQuestion } = useContext(PreviewContext);
  const { updateTemplateQuestion } = useContext(UserPreviewContext);

  const previousQuestionId: number = usePrevious(question.id);

  const initialParsed = useMemo(
    () => parseInitialPhone(question?.answer),
    [question?.answer],
  );

  const [countryCode, setCountryCode] = useState<string>(initialParsed.code);
  const [text, setText] = useState<string>(
    question.question_type === 'phone'
      ? countryCode === '+1'
        ? formatPhoneNumber(initialParsed.local)
        : initialParsed.local
      : question?.answer,
  );

  useEffect(() => {
    if (question?.id !== previousQuestionId) {
      setMounted(false);
      setCountryCode(initialParsed.code);
      setText(
        question.question_type === 'phone'
          ? countryCode === '+1'
            ? formatPhoneNumber(initialParsed.local)
            : initialParsed.local
          : question?.answer,
      );
      setError(null);
    }

    // Mount immediately so the input renders without the "+CC"
    setMounted(true);

    return () => setMounted(false);
  }, [question?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const debouncedText: string = useDebounce<string>(text, SAVE_TEXT_DELAY_MS);
  const timeoutRef = useRef<any>(null);
  const textLimit = question.number_of_characters
    ? parseInt(question.number_of_characters)
    : undefined;
  const textCount: number = useMemo(() => text?.length ?? 0, [text]);

  // Validation logic
  useEffect(() => {
    if (!isMounted) return;
    if (!question.question_type) {
      setError(null);
      return;
    }

    if (question.question_type === 'email') {
      setError(text && !validateEmail(text) ? 'Invalid email address' : null);
    } else if (question.question_type === 'phone') {
      const digits = (text || '').replace(/\D/g, '');
      const ccDigits = countryCode.replace('+', '');
      const total = digits.length + ccDigits.length;
      // E.164: up to 15 digits total; require at least 8 total digits and min local 4
      const isValid = total >= 8 && total <= 15 && digits.length >= 4;
      setError(text && !isValid ? 'Invalid phone number' : null);
    } else {
      setError(null);
    }
  }, [text, question.question_type, isMounted, countryCode]);

  useEffect(() => {
    const _onSave = () => {
      const answerParams = { questionId: question.id, answer: text };

      if (mode === 'admin-preview') return updatePreviewQuestion(answerParams);
      if (mode === 'admin-template') {
        return updateTemplateQuestion(answerParams);
      }

      return updateQuestion(answerParams);
    };

    const nextAnswer =
      question.question_type === 'phone' && (!text || text.trim() === '')
        ? ''
        : question.question_type === 'phone'
          ? `${countryCode} ${text}`.trim()
          : text;
    const shouldSave = isMounted && nextAnswer !== question.answer && !error;

    shouldSave && setSaved(true);
    shouldSave && _onSave();

    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => setSaved(false), 1000);

    return () => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [debouncedText, isBlurred, error, countryCode]); // eslint-disable-line react-hooks/exhaustive-deps

  const onTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    let value = event.target.value;

    if (question.question_type === 'phone') {
      const digitsOnly = value.replace(/\D/g, '');
      value = countryCode === '+1' ? formatPhoneNumber(digitsOnly) : digitsOnly;
    }

    setText(value);
  };

  const onBlur = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();

    setBlurred(true);
    onTextChange(event);
  };

  const customPlaceholder = useMemo(() => {
    if (question.question_type === 'email') return 'Email';
    if (question.question_type === 'phone') return 'Phone Number';

    return 'Your Answer';
  }, [question.question_type]);

  // Display only the local part (code shown in the selector), without modifying formatting
  const displayValue = useMemo(() => {
    if (question.question_type !== 'phone') return text;
    return text;
  }, [text, question.question_type]);

  return (
    <Flex gap={4} xAlign="center">
      {question.question_type === 'phone' && (
        <select
          disabled={isDisabled || !isMounted}
          value={countryCode}
          onChange={(e) => {
            const newCode = e.target.value;
            setCountryCode(newCode);

            // Normalize the local part when switching codes
            const digitsOnly = text.replace(/\D/g, '');
            setText(
              newCode === '+1' ? formatPhoneNumber(digitsOnly) : digitsOnly,
            );
          }}
          style={{
            height: 36,
            border: '1px solid #cbd5e1',
            borderRadius: 6,
            padding: '0 8px',
            background: '#fff',
          }}
        >
          {COUNTRY_OPTIONS.map((opt) => (
            <option key={opt.code} value={opt.code}>
              {opt.label}
            </option>
          ))}
        </select>
      )}
      <Input
        label="Answer"
        isLabelHidden={true}
        isDisabled={isDisabled || !isMounted}
        autoComplete="new-password"
        iconRightColor="success"
        placeholder={customPlaceholder}
        type={
          question.question_type === 'email'
            ? 'email'
            : question.question_type === 'phone'
              ? 'tel'
              : 'text'
        }
        id={question.id.toString()}
        iconRight={isSaved ? 'check-circle-filled' : undefined}
        value={displayValue}
        onBlur={onBlur}
        onChange={onTextChange}
        onFocus={() => setBlurred(false)}
        maxLength={textLimit}
        isError={!!error}
        helperText={
          error ||
          (textLimit
            ? textCount.toString() + '/' + textLimit.toString()
            : undefined)
        }
      />
    </Flex>
  );
};
